/**
 * Helpers de temps. Pures fonctions, aucune dependance.
 *
 * Convention du projet : les instants sont des chaines ISO 8601 **UTC**
 * (`...Z`), les jours des chaines `YYYY-MM-DD` en heure **locale**.
 */

import type { DayKey, Instant } from './types'

export const MINUTE_MS = 60_000
export const HEURE_MS = 3_600_000
export const JOUR_MS = 24 * HEURE_MS

export function nowInstant(): Instant {
  return new Date().toISOString()
}

export function toInstant(d: Date | number): Instant {
  return new Date(d).toISOString()
}

/** Millisecondes depuis l'epoch. `NaN` si la chaine est invalide. */
export function epoch(i: Instant): number {
  return Date.parse(i)
}

/** Jour calendaire local d'un instant, au format `YYYY-MM-DD`. */
export function dayKeyOf(i: Instant | Date | number): DayKey {
  const d = i instanceof Date ? i : new Date(i)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const j = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${j}`
}

export function todayKey(): DayKey {
  return dayKeyOf(new Date())
}

/** Decale une cle de jour de `n` jours (n peut etre negatif). */
export function shiftDayKey(key: DayKey, n: number): DayKey {
  const [y, m, d] = key.split('-').map(Number)
  const date = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1)
  date.setDate(date.getDate() + n)
  return dayKeyOf(date)
}

/** Heure **locale** de l'instant, 0-23. */
export function heureLocale(i: Instant): number {
  return new Date(i).getHours()
}

/**
 * Minutes ecoulees depuis minuit, en heure locale (0-1439).
 *
 * Sert aux statistiques d'heure de coucher et de lever. Attention : une moyenne
 * directe sur cette valeur est fausse pour les couchers, qui enjambent minuit
 * (23 h 50 et 00 h 10 ont pour moyenne 12 h 00 au lieu de minuit). Les
 * fonctions concernees passent par `minutesCentrees`.
 */
export function minutesDuJour(i: Instant): number {
  const d = new Date(i)
  return d.getHours() * 60 + d.getMinutes()
}

/**
 * Meme chose, mais rapportee a un axe continu centre sur la nuit : les heures
 * de l'apres-midi et du soir deviennent negatives, de sorte que 23 h 50 (-10)
 * et 00 h 10 (+10) aient bien pour moyenne minuit.
 *
 * Le pivot est 14 h : personne ne se couche ni ne se leve a cette heure-la, ce
 * qui garantit qu'aucune serie reelle n'est coupee en deux par la bascule.
 */
export function minutesCentrees(i: Instant): number {
  const m = minutesDuJour(i)
  return m >= 14 * 60 ? m - 24 * 60 : m
}

/** Inverse de `minutesCentrees`, pour reafficher une moyenne en heure murale. */
export function minutesCentreesVersHoraire(m: number): string {
  const norm = ((Math.round(m) % 1440) + 1440) % 1440
  const h = Math.floor(norm / 60)
  const min = norm % 60
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

/**
 * Valeur pour un `<input type="datetime-local">`, qui attend une heure
 * **locale** sans fuseau.
 */
export function toDatetimeLocalValue(i: Instant): string {
  const d = new Date(i)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

/** Inverse de `toDatetimeLocalValue` : interprete la saisie en heure locale. */
export function fromDatetimeLocalValue(v: string): Instant {
  return new Date(v).toISOString()
}

const DATE_FMT = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})

const DATE_COURTE_FMT = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
})

const HEURE_FMT = new Intl.DateTimeFormat('fr-FR', {
  hour: '2-digit',
  minute: '2-digit',
})

/** Une `DayKey` se formate en heure locale sans passer par `Date.parse`, qui
 *  interpreterait `2026-08-11` comme minuit **UTC** et pourrait reculer d'un
 *  jour selon le fuseau. */
function dateDeDayKey(key: DayKey): Date {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1)
}

export function formatJour(key: DayKey): string {
  return DATE_FMT.format(dateDeDayKey(key))
}

export function formatJourCourt(key: DayKey): string {
  return DATE_COURTE_FMT.format(dateDeDayKey(key))
}

export function formatDate(i: Instant): string {
  return DATE_COURTE_FMT.format(new Date(i))
}

export function formatHeure(i: Instant): string {
  return HEURE_FMT.format(new Date(i))
}

export function formatDateHeure(i: Instant): string {
  return `${formatDate(i)} à ${formatHeure(i)}`
}

/** Duree lisible : « 6 h 49 », « 42 min ». */
export function formatDuree(ms: number): string {
  const minutes = Math.max(0, Math.round(ms / MINUTE_MS))
  if (minutes < 60) return `${minutes} min`
  const heures = Math.floor(minutes / 60)
  const reste = minutes % 60
  return reste ? `${heures} h ${String(reste).padStart(2, '0')}` : `${heures} h`
}

/** Duree signee, pour une dette ou un ecart : « +1 h 10 », « −25 min ». */
export function formatEcart(ms: number): string {
  const signe = ms < 0 ? '−' : '+'
  return `${signe}${formatDuree(Math.abs(ms))}`
}

const RTF = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' })

/** « il y a 3 heures », « dans 2 jours »… */
export function relatif(i: Instant, base: number = Date.now()): string {
  const diff = epoch(i) - base
  const abs = Math.abs(diff)
  if (abs < MINUTE_MS) return "à l'instant"
  if (abs < HEURE_MS) return RTF.format(Math.round(diff / MINUTE_MS), 'minute')
  if (abs < JOUR_MS) return RTF.format(Math.round(diff / HEURE_MS), 'hour')
  if (abs < 30 * JOUR_MS) return RTF.format(Math.round(diff / JOUR_MS), 'day')
  return RTF.format(Math.round(diff / (30 * JOUR_MS)), 'month')
}
